import React, { useEffect, useState } from 'react';

import { useAppDispatch } from 'src/store';
import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import TypeToConfirmDialog from 'src/app/admin/components/type-to-confirm-dialog/TypeToConfirmDialog';
import BlastRadiusList from 'src/app/admin/components/blast-radius/BlastRadiusList';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import { useCustomerStatsQuery, useDeleteCustomerMutation } from 'src/app/admin/state/api/adminGraphSlice';
import {
    describeArchiveFailure,
    describeDeleteFailure,
    PURGE_FAILURE,
    REFUSED_AFTER_NATURE_RISK,
} from 'src/app/admin/state/api/describeDeleteFailure';
import { DeleteCustomerResult } from 'src/app/admin/state/api/queries/customerAdminQuery';
import {
    type ArchiveOrganisationOutcome,
    type PurgeOrganisationOutcome,
    type PurgePreview,
    useArchiveOrganisationMutation,
    usePurgeOrganisationMutation,
    useGetPurgePreviewQuery,
} from 'src/app/admin/views/risk-portfolio/state/api/organisationEngineSlice';

type DeleteOrganisationDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    customerId: string;
    customerName: string;
    /**
     * Called once the operator has read what went and dismissed it, not the moment the cascade
     * returns — a caller that tears this dialog down would take the summary with it.
     */
    onDeleted?: () => void;
};

type NatureRiskOutcome = ArchiveOrganisationOutcome | PurgeOrganisationOutcome;

type Receipt = {
    removed: DeleteCustomerResult['admin']['deleteCustomer'];
    natureRisk: NatureRiskOutcome;
    /** The preview figures shown before a purge, when they loaded. The purge itself reports no counts. */
    purgedCounts?: PurgePreview;
};

const NATURE_RISK_RECEIPT: Record<NatureRiskOutcome, string> = {
    archived: 'Nature Risk organisation archived.',
    purged: 'Nature Risk organisation and its data permanently removed.',
    none: 'No Nature Risk organisation was found (never set up, or already removed).',
};

const count = (value: number) => value.toLocaleString('en-GB');

const countOf = (value: number, singular: string, plural: string) => `${count(value)} ${value === 1 ? singular : plural}`;

const natureRiskReceipt = ({ natureRisk, purgedCounts }: Receipt) =>
    natureRisk === 'purged' && purgedCounts
        ? `Nature Risk organisation and its data permanently removed: ${countOf(purgedCounts.sites, 'site', 'sites')}, ` +
          `${countOf(purgedCounts.geographies, 'geography', 'geographies')}, ` +
          `${countOf(purgedCounts.activities, 'activity', 'activities')} and ` +
          `${countOf(purgedCounts.risk_insights_runs, 'risk insights run', 'risk insights runs')}.`
        : NATURE_RISK_RECEIPT[natureRisk];

const DeleteOrganisationDialog = ({ open, onOpenChange, customerId, customerName, onDeleted }: DeleteOrganisationDialogProps) => {
    const { data: stats, isLoading, isError } = useCustomerStatsQuery({ customerId }, { skip: !open });
    const dispatch = useAppDispatch();
    const [archiveOrganisation, { isLoading: isArchiving }] = useArchiveOrganisationMutation();
    const [purgeOrganisation, { isLoading: isPurging }] = usePurgeOrganisationMutation();
    const [deleteCustomer, { isLoading: isDeleting }] = useDeleteCustomerMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [receipt, setReceipt] = useState<Receipt | null>(null);
    const [permanentlyRemove, setPermanentlyRemove] = useState(false);
    const purgePreview = useGetPurgePreviewQuery(customerId, { skip: !open || !permanentlyRemove });

    useEffect(() => {
        if (!open) {
            setErrorMessage(null);
            setReceipt(null);
            setPermanentlyRemove(false);
        }
    }, [open]);

    const handleConfirm = async () => {
        setErrorMessage(null);
        const archived = await archiveOrganisation(customerId)
            .unwrap()
            .catch((error: unknown) => {
                setErrorMessage(describeArchiveFailure(error));
                return null;
            });
        if (!archived) {
            return;
        }
        const natureRisk: NatureRiskOutcome | null =
            permanentlyRemove && archived === 'archived'
                ? await purgeOrganisation(customerId)
                      .unwrap()
                      .catch(() => {
                          setErrorMessage(PURGE_FAILURE);
                          return null;
                      })
                : archived;
        if (!natureRisk) {
            return;
        }
        try {
            const result = await deleteCustomer({ customerId }).unwrap();
            setReceipt({
                removed: result.admin.deleteCustomer,
                natureRisk,
                purgedCounts: natureRisk === 'purged' ? purgePreview.data ?? undefined : undefined,
            });
        } catch (error) {
            const failure = describeDeleteFailure(error);

            // A rejected mutation invalidates nothing, so an organisation that has gone behind our
            // back would keep its row. Re-read the lists ourselves to match what we tell the operator.
            if (failure.alreadyGone) {
                dispatch(graphApiSlice.util.invalidateTags(['customer', 'project']));
            }

            setErrorMessage(failure.refused && natureRisk !== 'none' ? REFUSED_AFTER_NATURE_RISK[natureRisk] : failure.message);
        }
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && receipt) {
            // Deferred to here rather than done by the mutation: re-reading the customers list makes
            // the organisation page render "not found", unmounting this dialog and its summary with
            // it. The operator reads what went first; only then does the rest of the UI catch up.
            dispatch(graphApiSlice.util.invalidateTags(['customer', 'project']));
            onDeleted?.();
        }
        onOpenChange(nextOpen);
    };

    return (
        <TypeToConfirmDialog
            open={open}
            onOpenChange={handleOpenChange}
            entityLabel='organisation'
            confirmationText={customerName}
            confirmationPrompt={`Type the organisation name "${customerName}" to confirm`}
            warning='This removes every project, sample and portfolio beneath it, archives its Nature Risk sites and data, and takes away the access of everyone below. It cannot be undone.'
            blastRadius={
                <>
                    <BlastRadiusList
                        figures={[
                            { label: 'Projects', value: stats?.projectCount ?? 0 },
                            { label: 'Samples', value: stats?.sampleCount ?? 0 },
                            { label: 'Portfolios', value: stats?.portfolioCount ?? 0 },
                            {
                                label: 'Users losing access',
                                value: stats?.users.total ?? 0,
                                note: stats ? `${stats.users.internal} internal / ${stats.users.external} external` : undefined,
                            },
                        ]}
                        generatedAt={stats?.generatedAt}
                        isLoading={isLoading}
                        isError={isError}
                    />
                    <Checkbox checked={permanentlyRemove} onChange={setPermanentlyRemove} label='Permanently remove Nature Risk data' />
                    {permanentlyRemove && (
                        <div className='flex flex-col gap-2' data-testid='purge-preview'>
                            <p className='text-sm text-red-100'>
                                Its Nature Risk sites, their results and run history are removed for good, not archived.
                            </p>
                            {purgePreview.data === null ? (
                                <p className='text-sm text-grey-70'>
                                    No Nature Risk organisation was found, so there is nothing to remove.
                                </p>
                            ) : (
                                <BlastRadiusList
                                    figures={[
                                        { label: 'Sites', value: purgePreview.data?.sites ?? 0 },
                                        { label: 'Geographies', value: purgePreview.data?.geographies ?? 0 },
                                        { label: 'Activities', value: purgePreview.data?.activities ?? 0 },
                                        { label: 'Risk insights runs', value: purgePreview.data?.risk_insights_runs ?? 0 },
                                    ]}
                                    isLoading={purgePreview.isLoading}
                                    isError={purgePreview.isError}
                                />
                            )}
                        </div>
                    )}
                </>
            }
            confirmLabel='Delete organisation'
            onConfirm={handleConfirm}
            isConfirming={isArchiving || isPurging || isDeleting}
            errorMessage={errorMessage}
            // Given only when the cascade succeeded; anything else leaves the gate in place.
            receipt={
                receipt ? (
                    <p>
                        <b>Organisation &ldquo;{customerName}&rdquo; deleted:</b>
                        <br />
                        {count(receipt.removed.projectCount)} projects, {count(receipt.removed.sampleCount)} samples,{' '}
                        {count(receipt.removed.assetCount)} assets, {count(receipt.removed.chartCount)} charts and{' '}
                        {count(receipt.removed.portfolioCount)} portfolios.
                        <br />
                        {natureRiskReceipt(receipt)}
                    </p>
                ) : undefined
            }
        />
    );
};

export default DeleteOrganisationDialog;
